import { useCallback } from 'react';
import * as Cesium from 'cesium';
import { getColourArray } from '../../../../../../utils/commonFunctions';
import type { SatelliteGroupDataType } from '../../../../../modelling/types';
import { buildKeplerianSampledPosition } from '../../../lib/orbitPropagator';
import { ACCENT, MU } from '../constants';
import type { MapSatellite } from '../types';
import {
  buildBackendLlaSampledPosition,
  buildEphemerisSampledPosition,
  buildTleSampledPosition,
} from '../utils/orbitUtils';

export function useGroundTracks(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  modelClockWindowRef: React.MutableRefObject<{ startEpoch: number; endEpoch: number } | null>,
  orbitLeadTimeRef: React.MutableRefObject<number>,
  orbitTrailTimeRef: React.MutableRefObject<number>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  satOrbitActiveMap: React.MutableRefObject<Record<string, boolean>>,
  layersRef: React.MutableRefObject<any>,
  groundTrackTickSubs: React.MutableRefObject<Map<string, Cesium.Event.RemoveCallback>>,
  clearGroup: (group: string) => void,
) {
  const clearGroundTracks = useCallback(() => {
    groundTrackTickSubs.current.forEach(unsub => unsub());
    groundTrackTickSubs.current.clear();
    clearGroup('ground-tracks');
  }, [clearGroup]);

  const drawGroundTracks = useCallback((groups: SatelliteGroupDataType[]) => {
    const ds = sharedDataSourceRef.current;
    const v = viewerRef.current;
    if (!ds || !v) return;

    clearGroundTracks();
    if (!layersRef.current.groundTracks) return;

    const ids: string[] = [];
    const SEGMENT_SLOTS = 8;
    const SAMPLE_COUNT = 96;
    const MIN_WINDOW_SEC = 10 * 60;
    const CACHE_EPSILON_SEC = 5;

    type GroundTrackSegments = Cesium.Cartesian3[][];
    type CachedGroundTrack = {
      clockSec: number;
      leadSec: number;
      trailSec: number;
      segments: GroundTrackSegments;
    };

    const secondsFromJulianDate = (time: Cesium.JulianDate) => Cesium.JulianDate.toDate(time).getTime() / 1000;

    const splitNadirSegments = (points: Array<{ lon: number; lat: number }>): GroundTrackSegments => {
      const segments: GroundTrackSegments = [];
      let current: Cesium.Cartesian3[] = [];

      points.forEach((point, index) => {
        const prev = points[index - 1];
        const crossesDateline = !!prev && Math.abs(point.lon - prev.lon) > Math.PI;
        if (crossesDateline && current.length >= 2) {
          segments.push(current);
          current = [];
        } else if (crossesDateline) {
          current = [];
        }

        current.push(Cesium.Cartesian3.fromRadians(point.lon, point.lat, 100));
      });

      if (current.length >= 2) segments.push(current);
      return segments.slice(0, SEGMENT_SLOTS);
    };

    groups.forEach((group) => {
      group.satellites.forEach((rawSat) => {
        const sat = rawSat as MapSatellite;
        const tleLines = sat.tleLines;
        const ephemeris = sat.ephemeris;
        const backendOrbitTrack = sat.backendOrbitTrack;
        const op = sat.orbitParameters;

        let sampledPos: Cesium.SampledPositionProperty;
        let T: number;
        const win = modelClockWindowRef.current;
        const clockSec = secondsFromJulianDate(v.clock.currentTime);
        const leadTrailSec = Math.max(orbitLeadTimeRef.current + orbitTrailTimeRef.current, MIN_WINDOW_SEC);
        const fallbackBuildStartSec = clockSec - Math.max(leadTrailSec + 20 * 60, 2 * 60 * 60) / 2;
        const buildStartSec = win?.startEpoch ?? fallbackBuildStartSec;
        const buildEndSec = win?.endEpoch ?? (clockSec + Math.max(leadTrailSec + 20 * 60, 2 * 60 * 60) / 2);
        const baseBuildSpanSec = Math.max(leadTrailSec + 20 * 60, buildEndSec - buildStartSec, 2 * 60 * 60);
        const buildStart = new Date(buildStartSec * 1000);

        const backendBuilt = backendOrbitTrack?.orbitPoints?.length ? buildBackendLlaSampledPosition(backendOrbitTrack) : null;
        const ephemerisBuilt = !backendBuilt && ephemeris?.length ? buildEphemerisSampledPosition(ephemeris) : null;
        if (backendBuilt) {
          sampledPos = backendBuilt.position;
          T = backendBuilt.periodSec;
        } else if (ephemerisBuilt) {
          sampledPos = ephemerisBuilt.position;
          T = ephemerisBuilt.periodSec;
        } else if (tleLines) {
          const built = buildTleSampledPosition(
            tleLines.line1,
            tleLines.line2,
            buildStart,
            Math.max(1, Math.ceil(baseBuildSpanSec / 5400)),
            120,
          );
          sampledPos = built.position;
          T = built.periodSec;
        } else {
          if (!op?.semiMajorAxis || op.semiMajorAxis <= 0) return;
          const n = Math.sqrt(MU / Math.pow(op.semiMajorAxis, 3));
          T = (2 * Math.PI) / n;
          sampledPos = buildKeplerianSampledPosition(
            {
              semiMajorAxis: op.semiMajorAxis,
              eccentricity: op.eccentricity,
              inclination: op.inclination,
              RAAN: op.RAAN,
              AOP: op.AOP,
              TA: op.TA,
              epoch: op.epoch,
            },
            buildStart,
            Math.max(1, Math.ceil(baseBuildSpanSec / T)),
            480,
          );
        }

        const orbitColor = (() => {
          if (sat.orbitColour && !sat.orbitColour.includes('#')) {
            try {
              const arr = getColourArray(sat.orbitColour);
              return new Cesium.Color(arr[0] / 255, arr[1] / 255, arr[2] / 255, 1.0);
            } catch { /* fall through */ }
          }
          return ACCENT;
        })();

        let cache: CachedGroundTrack | null = null;
        const getSegments = (): GroundTrackSegments => {
          const currentTime = v.clock.currentTime;
          const currentClockSec = secondsFromJulianDate(currentTime);
          const desiredLeadSec = Math.max(orbitLeadTimeRef.current, 0);
          const desiredTrailSec = Math.max(orbitTrailTimeRef.current, 0);
          const desiredWindowSec = Math.max(desiredLeadSec + desiredTrailSec, MIN_WINDOW_SEC);
          const periodSec = Number.isFinite(T) && T > 0 ? T : desiredWindowSec;
          const maxWindowSec = Math.min(periodSec, Math.max(desiredWindowSec, Math.min(MIN_WINDOW_SEC, periodSec)));
          const trailRatio = desiredWindowSec > 0 ? desiredTrailSec / desiredWindowSec : 0.5;
          const trailSec = maxWindowSec * trailRatio;
          const leadSec = maxWindowSec - trailSec;

          if (
            cache &&
            Math.abs(cache.clockSec - currentClockSec) < CACHE_EPSILON_SEC &&
            Math.abs(cache.leadSec - leadSec) < 0.001 &&
            Math.abs(cache.trailSec - trailSec) < 0.001
          ) {
            return cache.segments;
          }

          const points: Array<{ lon: number; lat: number }> = [];
          for (let i = 0; i <= SAMPLE_COUNT; i++) {
            const offsetSec = -trailSec + ((leadSec + trailSec) * i) / SAMPLE_COUNT;
            const sampleJD = Cesium.JulianDate.addSeconds(currentTime, offsetSec, new Cesium.JulianDate());
            const ecef = sampledPos.getValue(sampleJD);
            if (!ecef) continue;
            const carto = Cesium.Cartographic.fromCartesian(ecef);
            if (!carto || !isFinite(carto.longitude) || !isFinite(carto.latitude)) continue;
            points.push({ lon: carto.longitude, lat: carto.latitude });
          }

          cache = {
            clockSec: currentClockSec,
            leadSec,
            trailSec,
            segments: points.length >= 2 ? splitNadirSegments(points) : [],
          };
          return cache.segments;
        };

        for (let slot = 0; slot < SEGMENT_SLOTS; slot++) {
          const segId = `ground-track_${sat.id}_${slot}`;
          ds.entities.add({
            id: segId,
            polyline: {
              positions: new Cesium.CallbackProperty(() => getSegments()[slot] ?? [], false),
              width: 0.8,
              arcType: Cesium.ArcType.GEODESIC,
              material: new Cesium.ColorMaterialProperty(orbitColor.withAlpha(0.75)),
              show: new Cesium.CallbackProperty(
                () => layersRef.current.groundTracks && satOrbitActiveMap.current[sat.id] !== false,
                false,
              ),
            },
          });
          ids.push(segId);
        }
      });
    });

    entityGroupMap.current.set('ground-tracks', ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearGroundTracks]);

  return {
    drawGroundTracks,
    clearGroundTracks,
  };
}
